import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import YesodLogo from '../components/YesodLogo';
import { useAuth } from '../context/AuthContext';
import '../styles/Auth.css';

const ResetPasswordConfirm = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState('');
  
  const { confirmPasswordReset } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Extract token from URL query parameters or hash
  useEffect(() => {
    console.log('Current location:', {
      pathname: location.pathname,
      search: location.search,
      hash: location.hash
    });

    // Try to get token from different possible locations in the URL
    const searchParams = new URLSearchParams(location.search);
    
    // Check for access_token in query params
    let accessToken = searchParams.get('access_token');
    
    // If not in search params, check the hash (fragment)
    if (!accessToken && location.hash) {
      const hashParams = new URLSearchParams(location.hash.substring(1));
      accessToken = hashParams.get('access_token');
    }
    
    // Also look for recovery token parameter that Supabase might use
    const recoveryToken = searchParams.get('token') || searchParams.get('recovery_token');
    
    if (accessToken) {
      console.log('Found access_token in URL');
      setToken(accessToken);
    } else if (recoveryToken) {
      console.log('Found recovery token in URL');
      setToken(recoveryToken);
    } else {
      console.error('No token found in URL');
      setError('Invalid password reset link. Please request a new one.');
    }
  }, [location]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate passwords match
    if (password !== confirmPassword) {
      return setError('Passwords do not match');
    }

    // Validate password length
    if (password.length < 6) {
      return setError('Password must be at least 6 characters');
    }

    setLoading(true);

    try {
      await confirmPasswordReset(token, password);
      setSuccess(true);
      setTimeout(() => {
        navigate('/');
      }, 3000);
    } catch (err) {
      console.error('Password reset error:', err);
      setError(err.response?.data?.message || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <YesodLogo />
      
      <div className="auth-header">
        <h1 className="auth-title">Set New Password</h1>
        <p className="auth-subtitle">
          Enter a new password for your account
        </p>
      </div>

      <div className="auth-form">
        {error && <div className="form-error">{error}</div>}
        {success && (
          <div className="form-success">
            Password successfully reset! You'll be redirected to the login page shortly.
          </div>
        )}
        
        {!success && token && (
          <form onSubmit={handleSubmit}>
            <div className="input-group">
              <input
                type="password"
                className="form-input"
                placeholder="New Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            
            <div className="input-group">
              <input
                type="password"
                className="form-input"
                placeholder="Confirm New Password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
            </div>
            
            <button 
              type="submit" 
              className="auth-button"
              disabled={loading}
            >
              {loading ? 'Setting Password...' : 'Reset Password'}
            </button>
          </form>
        )}

        <div className="auth-footer">
          <Link to="/" className="auth-link">
            Back to login
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordConfirm;